/**
 * Theme context - Manages color theme state with localStorage persistence
 * Supports automatic detection of system preferences
 */
import React from 'react';
import type { ReactNode } from 'react';
import { darkTheme, lightTheme } from './../../../styles';

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

// Utility functions for localStorage with expiration
const storageWithExpiry = {
  setItem: (key: string, value: string, ttl: number = 2592000000) => { // Default: 30 days
    const item = {
      value: value,
      expiry: Date.now() + ttl,
    };
    localStorage.setItem(key, JSON.stringify(item));
  },
  
  getItem: (key: string): string | null => {
    const itemStr = localStorage.getItem(key);
    
    if (!itemStr) return null;
    
    try {
      const item = JSON.parse(itemStr);
      const isExpired = Date.now() > item.expiry;
      
      if (isExpired) {
        localStorage.removeItem(key);
        return null;
      }
      
      return item.value;
    } catch (e) {
      // In case of parsing error, return raw value
      return itemStr;
    }
  }
};

//  CRÉER ET EXPORTER le contexte
const ThemeContext = React.createContext<ThemeContextType>({
  theme: 'dark',
  toggleTheme: () => {},
});

//  EXPORT EXPLICITE du contexte et du type
export { ThemeContext };
export type { ThemeContextType };

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    // Function to get initial theme (user preference or stored)
  const getInitialTheme = (): Theme => {
    // Check if theme is stored in localStorage
    const savedTheme = storageWithExpiry.getItem('theme') as Theme | null;
    if (savedTheme && (savedTheme === 'dark' || savedTheme === 'light')) {
      return savedTheme;
    }
    
    // Otherwise, use system preferences
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  const [theme, setTheme] = React.useState<Theme>('dark');

  // Initialize theme when component mounts
  React.useEffect(() => {
    setTheme(getInitialTheme());
  }, []);

  // Apply theme to HTML document
  React.useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add(darkTheme);
      root.classList.remove(lightTheme);
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
    } else {
      root.classList.add(lightTheme);
      root.classList.remove(darkTheme);
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
    }
    
    // Save theme in localStorage with expiration (1 year)
    storageWithExpiry.setItem('theme', theme, 365 * 24 * 60 * 60 * 1000);
  }, [theme]);

  // Function to toggle between themes
  const toggleTheme = () => {
    setTheme((prevTheme: Theme) => prevTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => React.useContext(ThemeContext);